import { boardIdOfView, type CanvasView, type CoreCanvasView } from '../../types';
import type { CatalogTab } from './catalogTabMeta';

// --- Canvas P2 (SC-2655) — canvas-view → CatalogTab bridge + nav metadata -------
//
// The workspace shell AND the URL both speak `CanvasView` (the P0 vocab:
// entities/history/…) after the SC-2670 route rename. This module bridges a view to
// the older internal `CatalogTab` vocabulary (`table`/`changes`/…) that the ARIA
// panel ids (`catalogTabId`/`catalogTabPanelId`) still key on. The map is an
// exhaustive `Record`, so adding a `CanvasView` member is a compile error until the
// mapping is filled in.

const VIEW_TO_TAB: Record<CoreCanvasView, CatalogTab> = {
  entities: 'table',
  attributes: 'attributes',
  findings: 'findings',
  profile: 'profile',
  history: 'changes',
};

/** Core canvas view (`entities`) → the internal `CatalogTab` its ARIA panel ids
 *  key on. Board views (`board:<id>`) have no `CatalogTab` — their tab-bar/ARIA
 *  wiring arrives with the View Library (SC-2873). */
export const viewToTab = (view: CoreCanvasView): CatalogTab => VIEW_TO_TAB[view];

/** A canvas view id → its URL path segment(s) under `/catalogs/:id/`. Core views
 *  are their own segment; a board view id maps to `boards/<boardId>` (the `:`
 *  form never appears in a URL). Inverse of CatalogLayout's route matching. */
export function canvasViewPath(view: CanvasView): string {
  const boardId = boardIdOfView(view);
  return boardId === null ? view : `boards/${encodeURIComponent(boardId)}`;
}

// The section order + glyphs (mirrors the mockup's `#wsNav`). The entities
// section's display label comes from the catalog's entity term
// ("Products"/"Athletes"/…) resolved at render, so it's `null` here and filled in
// by `CanvasViewTabs`; the rest are static.
export interface CanvasNavSection {
  view: CoreCanvasView;
  glyph: string;
  /** Static display label, or `null` when it's the catalog's entity term. */
  label: string | null;
}

export const CANVAS_NAV_SECTIONS: readonly CanvasNavSection[] = [
  { view: 'entities', glyph: '▦', label: null },
  { view: 'attributes', glyph: '≣', label: 'Attributes' },
  { view: 'findings', glyph: '✦', label: 'Findings' },
  { view: 'history', glyph: '⤿', label: 'History' },
  { view: 'profile', glyph: '◈', label: 'Profile' },
];
